import React, { useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
} from "@mui/material";
import { ScatterChart } from "@mui/x-charts/ScatterChart";
import { metricLabel } from "../../utils/metricOptions";
import { correlation } from "../../utils/stats";
import { useReportComparison } from "../../hooks/useReportData";
import type { ScatterBlock } from "../../api/types";
import type { ComparisonGroup } from "../../../../../../shared/api/types";

interface ScatterViewProps {
  block: ScatterBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

interface Point {
  x: number;
  y: number;
  id: string;
  label: string;
}

/**
 * Scatter of two metrics across segments — each comparison group is one point
 * (x = xMetric value, y = yMetric value). Groups missing either value are
 * skipped. A Pearson r label summarises the relationship.
 */
export const ScatterView: React.FC<ScatterViewProps> = ({
  block,
  clientId,
  branding,
  fill = false,
}) => {
  const theme = useTheme();
  const color = branding?.chartColor ?? branding?.primaryColor ?? theme.palette.primary.main;
  const query = useReportComparison(
    block.dimension,
    [block.xMetric, block.yMetric],
    block.filters,
    clientId,
  );

  // The comparison API's `department` dimension also returns "Dept::Role"
  // child rows (for the admin drill-down panel's expand-to-role view);
  // exclude them here so each department is a single point.
  const groups: ComparisonGroup[] = useMemo(
    () => (query.data?.data?.groups ?? []).filter((g) => !g.group_key.includes("::")),
    [query.data],
  );

  const points: Point[] = useMemo(() => {
    const pts: Point[] = [];
    for (const g of groups) {
      const x = g.values?.[block.xMetric];
      const y = g.values?.[block.yMetric];
      if (x !== null && x !== undefined && y !== null && y !== undefined) {
        pts.push({ x, y, id: g.group_key, label: g.group_label });
      }
    }
    return pts;
  }, [groups, block.xMetric, block.yMetric]);

  const r = useMemo(
    () => correlation(points.map((p) => [p.x, p.y] as [number, number])),
    [points],
  );

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Couldn't load scatter data.</Alert>;
  }

  if (points.length === 0) {
    return <Alert severity="info">No paired data for this period.</Alert>;
  }

  const xLabel = metricLabel(block.xMetric);
  const yLabel = metricLabel(block.yMetric);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {block.title && (
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {block.title}
          </Typography>
        )}
        <Chip
          label={`Pearson r = ${r.toFixed(2)} (n=${points.length})`}
          size="small"
          sx={{ fontWeight: 700 }}
        />
      </Box>

      <ScatterChart
        height={fill ? 420 : 340}
        series={[
          {
            label: `${yLabel} vs ${xLabel}`,
            data: points.map((p) => ({ x: p.x, y: p.y, id: p.id })),
            color,
            valueFormatter: (v) =>
              v ? `${points.find((p) => p.id === v.id)?.label ?? ""}` : "",
          },
        ]}
        xAxis={[{ label: xLabel }]}
        yAxis={[{ label: yLabel }]}
        grid={{ vertical: true, horizontal: true }}
      />
    </Box>
  );
};
